import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box, DialogContent, DialogActions, Button } from "@mui/material";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import HtmlEditor from "../../../components/HtmlEditor";
import Preloader from "../../../components/Preloader";

const MAIN_URL = import.meta.env.VITE_MAIN_URL;

export default function LicenseAgreementEditDialog({ row, onSaved }) {
  const api = useApi();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const params = new URLSearchParams({
        country: row.country,
        licenseAgreementId: row.licenseAgreementId,
        version: row.version,
      });
      const response = await api.fetch(`${MAIN_URL}licenses/license-agreements/text?${params.toString()}`, false, "GET");
      if (cancelled) {
        return;
      }
      if (response && response.isSuccessful) {
        setText(response.text ?? "");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [api, row]);

  function buildPayload() {
    return {
      country: row.country,
      licenseAgreementId: row.licenseAgreementId,
      version: row.version,
      text,
    };
  }

  async function handleSave() {
    setSubmitting(true);
    const response = await api.fetch(`${MAIN_URL}licenses/license-agreements/text`, buildPayload(), "PUT");
    setSubmitting(false);
    if (response && response.isSuccessful) {
      enqueueSnackbar(Translate.get("LicenseAgreementSaved"), { variant: "success" });
      if (onSaved) {
        onSaved();
      }
      navigate(-1);
    }
  }

  async function handleSaveAsNewVersion() {
    setSubmitting(true);
    const response = await api.fetch(`${MAIN_URL}licenses/license-agreements/new-version`, buildPayload(), "POST");
    setSubmitting(false);
    if (response && response.isSuccessful) {
      enqueueSnackbar(Translate.get("LicenseAgreementSaved"), { variant: "success" });
      if (onSaved) {
        onSaved();
      }
      navigate(-1);
    }
  }

  if (loading) {
    return <Preloader />;
  }

  return (
    <>
      <DialogContent>
        <Box sx={{ minWidth: 720, pt: 2 }}>
          <HtmlEditor
            htmlContent={text}
            allowImages
            onBlur={(content) => setText(content)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigate(-1)}>{Translate.get("Cancel")}</Button>
        <Button variant="outlined" onClick={handleSaveAsNewVersion} disabled={submitting}>
          {Translate.get("SaveAsNewVersion")}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting}>
          {Translate.get("Save")}
        </Button>
      </DialogActions>
    </>
  );
}
